import { createContext, useCallback, useContext, useState } from 'react'
import { toast } from 'react-toastify'

import api from 'services/api'
import {
  ImageUploadPhase,
  optimizeImageForUpload,
  runImageUploadTask
} from 'utils/imageUpload'

import { ModelItineraries } from 'models/ModelItineraries'

interface ICityData {
  name: string
  description: string
  coordinates: {
    latitude: number
    longitude: number
  }
  rate: number
  accommodations: IAccommodationData[]
  images: string[]
}

interface IAccommodationData {
  name: string
  rate: number
}

interface ItinerariesContextData {
  uploadFile(
    file: File,
    purpose?: string,
    onPhase?: (phase: ImageUploadPhase) => void
  ): Promise<string>
  favoriteItinerary(id: string): Promise<void>
  rateItinerary({ id, rate }: { id: string; rate: number }): Promise<void>
  createItinerary(itineraryData: ModelItineraries): Promise<boolean>
  setItineraries: React.Dispatch<React.SetStateAction<ModelItineraries[]>>
  itineraries: ModelItineraries[]
  updateItinerary(itineraryData: ModelItineraries): Promise<boolean>
  getItineraries(itineraryData: ModelItineraries): Promise<void>
  getAllItineraries(): Promise<ModelItineraries[]>
}

const ItinerariesContext = createContext<ItinerariesContextData>(
  {} as ItinerariesContextData
)

const withCityOrder = (itineraryData: any) => ({
  ...itineraryData,
  cities: Array.isArray(itineraryData?.cities)
    ? itineraryData.cities.map((city: any, index: number) => ({
        ...city,
        order: index + 1
      }))
    : itineraryData?.cities
})

export const ItinerariesProvider = ({ children }: any) => {
  const [itineraries, setItineraries] = useState<ModelItineraries[]>([])

  const uploadFile = useCallback(
    async (
      file: File,
      purpose = 'itinerary_photo',
      onPhase?: (phase: ImageUploadPhase) => void
    ) =>
      runImageUploadTask(async () => {
        try {
          const optimizedFile = await optimizeImageForUpload(file)
          onPhase?.('uploading')

          const formData = new FormData()
          formData.append('file', optimizedFile)
          formData.append('purpose', purpose)
          const response = await api.post('/users/uploadFile', formData)

          if (!response.data?.locationInS3) {
            throw new Error('A API não retornou a URL da imagem enviada')
          }

          onPhase?.('done')
          return response.data.locationInS3
        } catch (error: any) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            'Não foi possível enviar a imagem.'
          throw new Error(Array.isArray(message) ? message[0] : message)
        }
      }, onPhase),
    []
  )

  const getAllItineraries = useCallback(async () => {
    const response = await api.get('/itineraries')
    return response.data as ModelItineraries[]
  }, [])

  const getItineraries = useCallback(async (itineraryData: any) => {
    try {
      await api.get('/itineraries', { ...itineraryData })
    } catch {
      toast.error(
        'Não foi possível visualizar os roteiros, tente novamente mais tarde',
        { theme: 'dark' }
      )
    }
  }, [])

  const createItinerary = useCallback(async (itineraryData: any) => {
    try {
      const {
        id: _generatedId,
        authorId: _generatedAuthorId,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        ...createPayload
      } = itineraryData

      await api.post('/itineraries', {
        ...withCityOrder(createPayload),
        type: 'simple',
        made: Boolean(itineraryData.made === 'true')
      })
      toast.success('Roteiro criado com sucesso', { theme: 'dark' })
      return true
    } catch (error: any) {
      if (error?.response?.status === 401) {
        window.localStorage.removeItem('token')
        window.localStorage.removeItem('user')
        toast.error(
          'Sua sessão expirou. Faça login novamente para criar o roteiro.',
          { theme: 'dark' }
        )
        return false
      }

      const apiMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message.join(', ')
        : error?.response?.data?.message

      const isTechnicalServerMessage =
        !apiMessage ||
        /^internal server error$/i.test(apiMessage) ||
        error?.response?.status >= 500

      const friendlyMessage = isTechnicalServerMessage
        ? 'Não foi possível salvar o roteiro. Revise os campos obrigatórios e tente novamente.'
        : apiMessage

      toast.error(friendlyMessage, { theme: 'dark' })
      return false
    }
  }, [])

  const updateItinerary = useCallback(async (itineraryData: any) => {
    try {
      await api.patch(`/itineraries/${itineraryData.id}`, {
        ...withCityOrder(itineraryData),
        type: 'simple',
        made:
          typeof itineraryData.made === 'boolean'
            ? itineraryData.made
            : itineraryData.made === 'true'
      })
      toast.success('Roteiro editado com sucesso', { theme: 'dark' })
      return true
    } catch (error: any) {
      const message =
        error?.response?.status === 403
          ? 'Você só pode editar seus próprios roteiros.'
          : error?.response?.data?.message ||
            'Não foi possível editar o roteiro.'
      toast.error(message, { theme: 'dark' })
      return false
    }
  }, [])

  const favoriteItinerary = useCallback(async (id: string) => {
    try {
      const response = await api.post('/itineraries/user/favorite', {
        itinerary: id
      })
      toast.success(response.data.message, { theme: 'dark' })
    } catch (e: any) {
      toast.error(e.response.data.message, { theme: 'dark' })
    }
  }, [])

  const rateItinerary = useCallback(
    async ({ id, rate }: { id: string; rate: number }) => {
      await api.put('/itinerary-rate', { itineraryId: id, rate })
    },
    []
  )

  return (
    <ItinerariesContext.Provider
      value={{
        rateItinerary,
        favoriteItinerary,
        uploadFile,
        setItineraries,
        itineraries,
        createItinerary,
        updateItinerary,
        getItineraries,
        getAllItineraries
      }}
    >
      {children}
    </ItinerariesContext.Provider>
  )
}

export function useItineraries(): ItinerariesContextData {
  return useContext(ItinerariesContext)
}
